import React, { useState, useMemo } from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';
import {
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
} from '@mui/icons-material';

// ============================================================================
//  HierarchyTree — reusable multi-level expandable tree
//
//  Props:
//    tree         array of nodes: [{ name, cartons, pallets, kgs, fcls?, children? }, ...]
//    metric       current metric: 'cartons' | 'pallets' | 'kgs' | 'fcls'
//    metricOptions array of { id, label } — the toggle buttons shown above tree
//    onMetricChange  callback (newMetricId) => void
//    initiallyExpanded array of paths (e.g. ['/0']) — defaults expanded
//
//  Bulletproofed v2 — incoming `tree` is sanitized once via useMemo to strip
//  null/invalid entries recursively. Handles malformed backend responses
//  (e.g. NULL group keys from build_hierarchy()) without crashing.
// ============================================================================
export default function HierarchyTree({
  tree,
  metric: extMetric,
  metricOptions = [
    { id: 'cartons', label: '📦 Cartons' },
    { id: 'pallets', label: '🛒 Pallets' },
    { id: 'kgs',     label: '⚖ KG' },
  ],
  onMetricChange,
  initiallyExpanded = [],
  showTotal = true,
}) {
  const [internalMetric, setInternalMetric] = useState(metricOptions[0]?.id || 'cartons');
  const metric = extMetric ?? internalMetric;

  const setMetric = (m) => {
    setInternalMetric(m);
    onMetricChange?.(m);
  };

  // ------------------------------------------------------------------------
  // Sanitize incoming tree once. Strips null / non-object nodes recursively,
  // ensures `children` is always an array, and supplies a name fallback.
  // ------------------------------------------------------------------------
  const safeTree = useMemo(() => {
    const clean = (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes
        .filter(n => n && typeof n === 'object')
        .map(n => ({
          ...n,
          name: (n.name ?? n.label ?? '(unspecified)') + '',
          children: clean(n.children),
        }));
    };
    return clean(tree);
  }, [tree]);

  const [expanded, setExpanded] = useState(() => {
    const init = {};
    initiallyExpanded.forEach(p => { init[p] = true; });
    return init;
  });

  const toggle = (path) => setExpanded(prev => ({ ...prev, [path]: !prev[path] }));

  const expandAll = () => {
    const all = {};
    const walk = (nodes, prefix) => nodes.forEach((n, i) => {
      const path = prefix + '/' + i;
      all[path] = true;
      if (n.children && n.children.length > 0) walk(n.children, path);
    });
    walk(safeTree, '');
    setExpanded(all);
  };

  const collapseAll = () => setExpanded({});

  // Compute total over sanitized tree
  const total = useMemo(() => {
    return safeTree.reduce((acc, n) => ({
      cartons: (acc.cartons || 0) + (n.cartons || 0),
      pallets: (acc.pallets || 0) + (n.pallets || 0),
      kgs:     (acc.kgs     || 0) + (n.kgs     || 0),
      fcls:    Math.max(acc.fcls || 0, n.fcls || 0),
    }), {});
  }, [safeTree]);

  const totalValue = total[metric] || 0;
  const fmt = (v) => metric === 'kgs' ? (v / 1000).toFixed(1) + 'k' : Math.round(v).toLocaleString();

  const renderNode = (node, level, path) => {
    const isOpen = !!expanded[path];
    const hasChildren = !!node.children && node.children.length > 0;
    const value = node[metric] || 0;
    const pct = totalValue > 0 ? (value / totalValue) * 100 : 0;

    const indent = 4 + level * 26;
    const colorMap = ['#0f172a', '#1e293b', '#475569', '#94a3b8'];
    const fontMap = [13, 12.5, 12, 11.5];
    const weightMap = [700, 600, 500, 500];

    return (
      <React.Fragment key={path}>
        <Box
          onClick={() => hasChildren && toggle(path)}
          sx={{
            display: 'flex', alignItems: 'center',
            py: 0.875, px: 1, pl: `${indent}px`,
            borderBottom: '1px solid', borderColor: 'divider',
            cursor: hasChildren ? 'pointer' : 'default',
            transition: 'background 0.12s',
            background: level === 0 ? 'linear-gradient(90deg, rgba(21,101,192,0.05), transparent)' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ width: 18, color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
            {hasChildren ? (isOpen ? <DownIcon sx={{ fontSize: 16 }} /> : <RightIcon sx={{ fontSize: 16 }} />) : '·'}
          </Box>
          <Box sx={{
            flex: 1, fontSize: fontMap[level] || 11.5, fontWeight: weightMap[level] || 500,
            color: colorMap[level] || '#94a3b8',
          }}>
            {node.name}
          </Box>
          <Box sx={{
            flex: 1, maxWidth: 180, height: 6, mx: 1.5, borderRadius: 1.5,
            bgcolor: 'action.hover', overflow: 'hidden',
          }}>
            <Box sx={{
              width: pct + '%', height: '100%',
              background: 'linear-gradient(135deg, #d32f2f, #1565c0)',
              borderRadius: 1.5,
            }} />
          </Box>
          <Box sx={{
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontWeight: 700, minWidth: 80, textAlign: 'right',
            fontSize: fontMap[level] || 11.5,
          }}>
            {fmt(value)}
          </Box>
          <Box sx={{
            fontSize: 10.5, color: 'text.disabled',
            minWidth: 44, textAlign: 'right', ml: 0.75,
          }}>
            {pct.toFixed(1)}%
          </Box>
        </Box>
        {isOpen && hasChildren && node.children.map((c, i) => renderNode(c, level + 1, path + '/' + i))}
      </React.Fragment>
    );
  };

  return (
    <Box>
      {/* Metric toggle + Expand/Collapse */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
          {metricOptions.map(m => (
            <Button
              key={m.id} size="small"
              onClick={() => setMetric(m.id)}
              sx={{
                fontSize: 11.5, fontWeight: 600, textTransform: 'none', py: 0.25, px: 1.5,
                borderRadius: 0.75,
                ...(metric === m.id
                  ? { bgcolor: 'background.paper', color: 'primary.main', boxShadow: 1 }
                  : { color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }),
              }}
            >
              {m.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={expandAll} sx={{ fontSize: 11, textTransform: 'none' }}>Expand all</Button>
          <Button size="small" onClick={collapseAll} sx={{ fontSize: 11, textTransform: 'none' }}>Collapse all</Button>
        </Box>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {safeTree.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled', fontSize: 12.5, fontStyle: 'italic' }}>
            No data for the selected period.
          </Box>
        ) : (
          <>
            {safeTree.map((b, i) => renderNode(b, 0, '/' + i))}
            {showTotal && (
              <Box sx={{
                display: 'flex', alignItems: 'center', py: 1, px: 1, pl: 0.5,
                bgcolor: 'action.hover', fontWeight: 700, color: 'text.primary',
              }}>
                <Box sx={{ width: 18 }} />
                <Box sx={{ flex: 1, fontSize: 13, fontWeight: 700, pl: 0.5 }}>TOTAL</Box>
                <Box sx={{ flex: 1, maxWidth: 180, mx: 1.5 }} />
                <Box sx={{
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  fontWeight: 700, minWidth: 80, textAlign: 'right', fontSize: 13,
                }}>
                  {fmt(totalValue)}
                </Box>
                <Box sx={{ fontSize: 10.5, color: 'text.disabled', minWidth: 44, textAlign: 'right', ml: 0.75 }}>
                  100%
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
